import * as React from 'react'

import { cn } from '#/shared/lib/utils'
import { Label } from '#/shared/ui/label'
import type { LabelSize } from '#/shared/ui/label'

interface TextareaProps extends React.ComponentProps<'textarea'> {
  label?: string
  labelSize?: LabelSize
  error?: boolean
  textareaSize?: 'default' | 'sm'
  resize?: 'none' | 'vertical' | 'horizontal' | 'both'
}

const sizeClasses = {
  default: 'min-h-22 p-2.5 text-sm',
  sm: 'min-h-16 p-2 text-sm',
} as const

const resizeClasses = {
  none: 'resize-none',
  vertical: 'resize-y',
  horizontal: 'resize-x',
  both: 'resize',
} as const

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      labelSize = 'default',
      id,
      error = false,
      textareaSize = 'default',
      resize = 'none',
      disabled,
      ...props
    },
    ref,
  ) => {
    const reactId = React.useId()
    const textareaId = id ?? reactId

    const textarea = (
      <textarea
        id={textareaId}
        ref={ref}
        disabled={disabled}
        className={cn(
          'w-full rounded-md border bg-white text-gray-900 placeholder:text-gray-300 focus-visible:outline-none focus-visible:focus-ring',
          sizeClasses[textareaSize],
          resizeClasses[resize],
          error
            ? 'border-red-500 focus-visible:focus-ring-error'
            : 'border-gray-200',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
        {...props}
      />
    )

    if (!label) return textarea

    return (
      <div
        className={cn(
          'flex flex-col',
          labelSize === 'sm' ? 'gap-0.5' : 'gap-1',
        )}
      >
        <Label htmlFor={textareaId} size={labelSize}>
          {label}
        </Label>
        {textarea}
      </div>
    )
  },
)
Textarea.displayName = 'Textarea'

export { Textarea }
export type { TextareaProps }
